/**
 * Formats a date to show the relative time from now (e.g., "5 minutes ago", "2 hours ago")
 * @param date The date to format
 * @returns A string representing the relative time
 */
export function formatDistanceToNow(date: Date): string {
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return 'just now'
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`
  }

  // For older dates, return the actual date
  return date.toLocaleDateString()
}

/**
 * Formats a date as YYYY-MM-DD string for comparison purposes (timezone-safe)
 * Uses local timezone to ensure consistent date interpretation
 * @param date The date to format
 * @returns A string in YYYY-MM-DD format
 */
export function formatDateForComparison(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Creates a date range that ensures proper timezone handling for database queries
 * Extends the end date to include the full day in any timezone
 * @param startDate Start date of the range
 * @param endDate End date of the range
 * @returns Object with formatted start and end dates for database queries
 */
export function createDateRangeForQuery(startDate: Date, endDate: Date) {
  // For the start date, use the beginning of the day in local timezone
  const adjustedStart = new Date(startDate)
  adjustedStart.setHours(0, 0, 0, 0)

  // For the end date, add one day and use beginning of that day to ensure
  // we capture all workouts scheduled for the end date regardless of timezone
  const adjustedEnd = new Date(endDate)
  adjustedEnd.setDate(adjustedEnd.getDate() + 1)
  adjustedEnd.setHours(0, 0, 0, 0)

  return {
    startDate: formatDateForComparison(adjustedStart),
    // Use 'lt' (less than) instead of 'lte' with the next day to avoid timezone issues
    endDate: formatDateForComparison(adjustedEnd),
  }
}

/**
 * Formats a date as timezone-safe ISO string with local date components
 * Prevents timezone shifts by using local date values instead of UTC conversion
 * @param date The date to format
 * @returns An ISO string in YYYY-MM-DDTHH:mm:ss.sssZ format using local date
 */
export function formatDateToISO(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}T00:00:00.000Z`
}

export function formatDateToLocalTime(date: Date): string {
  return date.toLocaleString('de-CH', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/**
 * Formats a time string (HH:MM) to a localized time format
 * @param time Time string in HH:MM format
 * @returns Formatted time string or null if no time provided
 */
export function formatTime(time?: string | null): string | null {
  if (!time) return null
  const [hours, minutes] = time.split(':')
  const timeObj = new Date()
  timeObj.setHours(parseInt(hours), parseInt(minutes))
  return timeObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Formats a date string to a long format with weekday, month, and day
 * @param dateString Date string to format
 * @returns Formatted date string
 */
export function formatLongDate(dateString?: string): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Formats a display date with optional time for form displays
 * @param dateString Date string to format
 * @param timeString Optional time string in HH:MM format
 * @returns Formatted display string
 */
export function formatDisplayDate(dateString: string, timeString?: string): string {
  if (!dateString) return 'Select date'

  const date = new Date(dateString)
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }

  let formatted = date.toLocaleDateString('en-US', options)

  if (timeString) {
    const formattedTime = formatTime(timeString)
    if (formattedTime) {
      formatted += ` at ${formattedTime}`
    }
  }

  return formatted
}

export function formatDurationMin(minutes: number): string {
  if (minutes < 60) {
    return `${Math.floor(minutes).toFixed(0)} min`
  }

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = Math.floor(minutes % 60)

  if (remainingMinutes === 0) {
    return `${hours}h`
  }

  return `${hours}h ${remainingMinutes}min`
}

export function formatMMSS(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

// Helper function to format time as mm:ss
export const formatTimeSeconds = (seconds: number): string => {
  const roundedSeconds = Math.round(seconds)
  const mins = Math.floor(roundedSeconds / 60)
  const secs = Math.floor(roundedSeconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Helper function to format pace as mm:ss per km
export const formatPace = (paceMinPerKm: number): string => {
  const mins = Math.floor(paceMinPerKm)
  const secs = Math.round((paceMinPerKm - mins) * 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Helper function to format swim pace as mm:ss per 100m
export const formatSwimPace = (paceMinPer100m: number): string => {
  const mins = Math.floor(paceMinPer100m)
  const secs = Math.round((paceMinPer100m - mins) * 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
